import { useState, useEffect } from "react";
import { trackEvent } from "@/components/Analytics";
import SEO from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Loader2, CheckCircle2, ShieldAlert, Zap, Clock, FileSearch,
  AlertTriangle, X, BadgePoundSterling, Hammer
} from "lucide-react";
import api from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";

const CONCERNS = [
  "Seems expensive",
  "Not sure if work is needed",
  "Want a second opinion",
  "Comparing multiple quotes",
  "Insurance-related quote",
];

const CHECKS = [
  "Is the work actually needed?",
  "Is the price reasonable?",
  "Is the scope clear?",
  "Is anything missing?",
  "Is anything excessive?",
  "Could insurance be relevant?",
  "Is an inspection recommended?",
];

export default function QuoteReview() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    park_name: "", plot_number: "", town_location: "",
    quote_value: "", contractor_name: "", type_of_works: "",
    main_concern: CONCERNS[0],
    description: "",
  });
  const [uploadIds, setUploadIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quote_value: form.quote_value ? Number(form.quote_value) : null,
        upload_ids: uploadIds,
        consent_to_contact: consent,
      };
      await api.post("/support/quote-review", payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      trackEvent("quote_review_submit", {
        value: Number(form.quote_value) || 0,
        currency: "GBP",
        high_value: (Number(form.quote_value) || 0) > 1000,
      });
      toast.success("Quote received. We'll review and reply.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return <Confirmation name={form.full_name} email={form.email} quoteValue={form.quote_value} />;

  return (
    <div data-testid="quote-review-page">
      <SEO
        title="Upload Your Quote for Review | Park Home Support"
        description="Before you approve expensive park home work, upload the quote for independent review. Quote reviews are included as part of PHS membership — alongside an Annual External Park Home Health Check, technical support and insurance guidance."
        keywords="park home quote review, park home health check, check park home repair quote, park home quote second opinion, park home repair cost review UK"
        canonical="https://parkhomesupport.co.uk/quote-review"
      />
      {/* HERO */}
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span className="eyebrow">Quote Review</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl text-[#071A33] leading-[1.05]">
                Upload Your Quote — <span className="text-[#064E2E]">We'll Tell You If It Looks Right.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-[#334155]">
                Before you approve expensive work, we review the quote, the scope and the likely need for the repairs.
              </p>

              <div className="mt-8 card-default border-l-4 border-[#B88A2A] bg-[#FFF8EC]" data-testid="qr-warning-card">
                <div className="flex gap-3 items-start">
                  <AlertTriangle size={22} className="text-[#B88A2A] mt-0.5 shrink-0" />
                  <p className="text-[#3F2E10] font-medium leading-relaxed">
                    Once work starts, it becomes much harder to challenge the cost, scope or quality.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-default" data-testid="qr-checks-card">
                <span className="eyebrow">What We Check</span>
                <h3 className="font-display text-2xl mt-2 text-[#071A33]">Seven specialist questions</h3>
                <ul className="mt-5 space-y-2.5 text-[#334155]">
                  {CHECKS.map((c) => (
                    <li key={c} className="flex gap-2.5"><CheckCircle2 size={18} className="text-[#0F766E] mt-0.5 shrink-0"/> {c}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="section-pad bg-white">
        <div className="container-phs max-w-3xl">
          <form className="card-default" onSubmit={onSubmit} data-testid="quote-review-form">
            <h2 className="font-display text-2xl text-[#071A33]">Get Your Quote Checked</h2>
            <p className="text-sm text-[#475569] mt-1">Tell us about the quote, the home and your concern. Upload the contractor estimate, photos or videos.</p>

            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <Field label="Full name" required><input className="field-input" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="qr-full-name" /></Field>
              <Field label="Email" required><input className="field-input" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="qr-email" /></Field>
              <Field label="Phone" required><input className="field-input" required value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="qr-phone" /></Field>
              <Field label="Park name" required><input className="field-input" required value={form.park_name} onChange={(e) => update("park_name", e.target.value)} data-testid="qr-park-name" /></Field>
              <Field label="Plot number" required><input className="field-input" required value={form.plot_number} onChange={(e) => update("plot_number", e.target.value)} data-testid="qr-plot-number" /></Field>
              <Field label="Town / location"><input className="field-input" value={form.town_location} onChange={(e) => update("town_location", e.target.value)} data-testid="qr-town" /></Field>
              <Field label="Quote value (£)"><input className="field-input" type="number" min="0" step="1" value={form.quote_value} onChange={(e) => update("quote_value", e.target.value)} data-testid="qr-quote-value" /></Field>
              <Field label="Contractor name"><input className="field-input" value={form.contractor_name} onChange={(e) => update("contractor_name", e.target.value)} data-testid="qr-contractor" /></Field>
              <Field label="Type of works" wide><input className="field-input" placeholder="e.g. roof repair, re-levelling, skirting" value={form.type_of_works} onChange={(e) => update("type_of_works", e.target.value)} data-testid="qr-type-of-works" /></Field>
              <Field label="Main concern" required wide>
                <select className="field-input" required value={form.main_concern} onChange={(e) => update("main_concern", e.target.value)} data-testid="qr-main-concern">
                  {CONCERNS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <div className="mt-6">
              <label className="field-label">Describe the issue or what worries you about the quote <span className="text-[#B88A2A]">*</span></label>
              <textarea className="field-input" required value={form.description} onChange={(e) => update("description", e.target.value)} data-testid="qr-description" />
            </div>

            <div className="mt-6">
              <FileUpload label="Upload the quote PDF, photos or videos" testId="qr-upload" onChange={setUploadIds} />
            </div>

            <label className="mt-6 flex items-start gap-3 text-sm text-[#334155] cursor-pointer" data-testid="qr-consent-wrapper">
              <input type="checkbox" className="mt-1" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="qr-consent-checkbox"/>
              <span>
                I agree to be contacted by Park Home Support regarding my enquiry by email, phone or SMS. I can opt out at any time by replying STOP to any SMS or emailing us. See our <a href="/privacy" className="link-underline">Privacy Policy</a>.
              </span>
            </label>

            <button disabled={submitting} className="btn-primary mt-7" data-testid="qr-submit-btn">
              {submitting ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : <>Submit Quote for Review <ArrowRight size={16} /></>}
            </button>
            <p className="mt-3 text-xs text-[#475569]">
              By submitting you start the service. <Link to="/terms" className="link-underline">Terms apply</Link>.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, wide, children }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}

function Confirmation({ name, email, quoteValue }) {
  const [memberStatus, setMemberStatus] = useState("LOADING");
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    if (!email) { setMemberStatus("NOT_FOUND"); return; }
    api.get("/members/status", { params: { email } })
      .then((r) => setMemberStatus(r.data.membership_status || "PENDING"))
      .catch(() => setMemberStatus("NOT_FOUND"));
  }, [email]);

  const isActiveMember = memberStatus === "ACTIVE";
  const valueNum = Number(quoteValue) || 0;
  const showSavingsHint = valueNum >= 1000;

  const goJoin = () => {
    const qs = new URLSearchParams({ source: "quote-review", email: email || "", name: name || "" });
    navigate(`/membership?${qs.toString()}`);
  };

  return (
    <div data-testid="qr-confirmation">
      <section className="bg-[#F7F4ED] pt-16 pb-10">
        <div className="container-phs max-w-4xl">
          <div className="card-default flex flex-col sm:flex-row gap-5 sm:items-center" data-testid="qr-status-card">
            <div className="w-14 h-14 bg-[#064E2E] text-white rounded-2xl flex items-center justify-center shrink-0">
              <CheckCircle2 size={28} />
            </div>
            <div className="flex-1">
              <span className="eyebrow">Quote received</span>
              <h1 className="font-display text-2xl sm:text-3xl mt-2 text-[#071A33] leading-tight">
                Your Quote Has Been Received{name ? `, ${name.split(" ")[0]}` : ""}.
              </h1>
              <p className="mt-2 text-[#334155]">
                We'll review the information provided and assess whether the scope, cost or supporting evidence raises any concerns.
              </p>
            </div>
            {isActiveMember && (
              <div className="bg-[#064E2E] text-white px-4 py-3 rounded-full text-sm font-medium flex items-center gap-2" data-testid="qr-priority-active">
                <Zap size={16} className="text-[#B88A2A]" /> Priority review activated
              </div>
            )}
          </div>
        </div>
      </section>

      {isActiveMember ? (
        <section className="section-pad bg-white">
          <div className="container-phs max-w-3xl text-center">
            <Zap className="mx-auto text-[#B88A2A]" size={36} />
            <h2 className="font-display text-3xl mt-4 text-[#071A33]">Your review is prioritised</h2>
            <p className="mt-3 text-[#334155]">As an active member, your case is queued ahead of non-members. You'll receive full breakdown, cost validation and next-step guidance.</p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              <Link to="/request-support" className="btn-outline" data-testid="qr-after-request-btn">Submit another issue</Link>
              <Link to="/" className="btn-ghost" data-testid="qr-after-home-btn">Back to Home</Link>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="bg-white">
            <div className="container-phs max-w-4xl py-10">
              <div className="card-dark border-l-4 border-[#B88A2A] flex gap-4 items-start" data-testid="qr-risk-callout">
                <ShieldAlert size={26} className="text-[#B88A2A] mt-0.5 shrink-0" />
                <div>
                  <p className="font-display text-xl sm:text-2xl leading-tight">
                    Want full support with this issue?
                  </p>
                  <p className="mt-2 text-white/85 text-sm sm:text-base">
                    {showSavingsHint
                      ? `Your quote (£${valueNum.toLocaleString()}) is exactly the size where over-scoping and over-pricing happen most. Members get the full breakdown — non-members receive limited guidance only.`
                      : "Join today for £99 and receive your Annual External Park Home Health Check, full quote review support, year-round technical and insurance guidance, and reduced rates on additional inspections."}
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="section-pad bg-[#F7F4ED]">
            <div className="container-phs max-w-5xl">
              <div className="text-center max-w-2xl mx-auto">
                <span className="eyebrow">What happens next</span>
                <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">
                  Free guidance — or full priority review?
                </h2>
                <p className="mt-4 text-[#334155]">
                  Both reach a real specialist. The level of detail, speed and ongoing protection differs.
                </p>
              </div>

              <div className="mt-10 grid md:grid-cols-2 gap-6">
                <article className="card-default flex flex-col" data-testid="qr-tier-non-member">
                  <span className="eyebrow text-[#475569]">Non-member</span>
                  <h3 className="font-display text-2xl mt-2 text-[#071A33]">Limited guidance</h3>
                  <p className="mt-2 text-sm text-[#475569]">Free. Standard queue.</p>
                  <ul className="mt-6 space-y-2.5 text-sm text-[#334155]">
                    {[
                      ["Standard review queue", true],
                      ["Headline guidance only — direction, not detail", true],
                      ["Cost validation broken down line-by-line", false],
                      ["Specialist-written response with next steps", false],
                      ["Ongoing support if more issues arise", false],
                      ["Reduced inspection rates", false],
                    ].map(([label, yes]) => (
                      <li key={label} className="flex gap-2.5">
                        {yes
                          ? <CheckCircle2 size={16} className="text-[#475569] mt-0.5 shrink-0" />
                          : <X size={16} className="text-[#B88A2A] mt-0.5 shrink-0" />}
                        <span className={yes ? "" : "text-[#475569] line-through decoration-[#B88A2A]/60"}>{label}</span>
                      </li>
                    ))}
                  </ul>
                </article>

                <article className="card-dark relative flex flex-col" data-testid="qr-tier-member">
                  <span className="absolute top-4 right-4 bg-[#B88A2A] text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full">
                    Priority
                  </span>
                  <span className="text-[#B88A2A] tracking-[0.22em] uppercase text-xs font-medium">Member</span>
                  <h3 className="font-display text-2xl mt-2">Full priority review</h3>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="price-display text-4xl">
                      <span className="price-currency">£</span>
                      <span className="price-value">99</span>
                    </span>
                    <span className="text-white/70 text-sm">first year, then £249/yr</span>
                  </div>
                  <ul className="mt-6 space-y-2.5 text-sm text-white/90">
                    {[
                      "Annual External Park Home Health Check included",
                      "Priority review queue (ahead of non-members)",
                      "Full line-by-line cost validation",
                      "Specialist-written response with next steps",
                      "Ongoing support for further issues for 12 months",
                      "Insurance-related technical guidance",
                      "Maintenance responsibility advice",
                      "Reduced rates for additional inspections",
                    ].map((x) => (
                      <li key={x} className="flex gap-2.5">
                        <CheckCircle2 size={16} className="text-[#B88A2A] mt-0.5 shrink-0" />{x}
                      </li>
                    ))}
                  </ul>
                  <button onClick={goJoin} className="btn-accent mt-7 w-full" data-testid="qr-upsell-join-btn">
                    Join Now for £99 <ArrowRight size={16} />
                  </button>
                  <p className="text-[11px] text-white/60 mt-3">
                    Renews at £249/year. No refunds once service has been used.
                  </p>
                </article>
              </div>

              <div className="mt-12 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="card-default text-center py-5"><Clock className="mx-auto text-[#0F766E]" size={22}/><p className="mt-2 font-display text-base text-[#071A33]">Faster turnaround</p></div>
                <div className="card-default text-center py-5"><FileSearch className="mx-auto text-[#0F766E]" size={22}/><p className="mt-2 font-display text-base text-[#071A33]">Detailed breakdown</p></div>
                <div className="card-default text-center py-5"><BadgePoundSterling className="mx-auto text-[#0F766E]" size={22}/><p className="mt-2 font-display text-base text-[#071A33]">Reduced inspection rates</p></div>
              </div>

              <div className="mt-10 text-center">
                <Link to="/" className="btn-ghost" data-testid="qr-back-home-link">
                  No thanks, I'll wait for the limited free guidance →
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
